import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { FileBadge, Send } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";

const render = (body, params) => body.replace(/\{\{(\d+)\}\}/g, (_, n) => params[n - 1] || `{{${n}}}`);

export const TemplateDialog = ({ conversation: c, highlight, onSent }) => {
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState(null);
  const [params, setParams] = useState([]);
  const [busy, setBusy] = useState(false);
  const { data: templates = [] } = useQuery({ queryKey: ["templates"], queryFn: () => api.get("/templates").then((r) => r.data), enabled: open });
  const approved = useMemo(() => templates.filter((t) => t.status === "APPROVED"), [templates]);

  const choose = (t) => {
    setSel(t);
    setParams(Array.from({ length: t.var_count }, (_, i) => (i === 0 ? (c.name || "").split(" ")[0] : "")));
  };
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/conversations/${c.id}/template`, { template_id: sel.id, params });
      if (data.delivery === "failed") toast.error(`Invio WhatsApp fallito: ${data.error}`);
      else toast.success("Modello inviato");
      setOpen(false); setSel(null); onSent();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <>
      <button data-testid="template-open-button" type="button" onClick={() => setOpen(true)} title="Invia modello approvato"
        className={`h-11 shrink-0 rounded-full flex items-center gap-1.5 transition-colors ${highlight ? "px-3 sm:px-4 bg-slate-900 text-white hover:bg-slate-800 text-xs font-semibold" : "w-11 justify-center text-slate-500 hover:bg-slate-100 hover:text-slate-800"}`}>
        <FileBadge size={18} />{highlight && <span className="hidden sm:inline">Usa un modello</span>}
      </button>
      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setSel(null); }}>
        <DialogContent className="max-w-2xl" data-testid="template-dialog">
          <DialogHeader>
            <DialogTitle>Modelli approvati Meta</DialogTitle>
            <DialogDescription>I modelli permettono di ricontattare il cliente anche dopo 24 ore dall'ultimo messaggio.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-[220px_1fr] gap-4 min-h-[280px]">
            <ul className="border border-slate-200 rounded-lg divide-y divide-slate-100 overflow-y-auto max-h-80 thin-scroll">
              {approved.length === 0 && <li className="p-4 text-sm text-slate-400">Nessun modello approvato</li>}
              {approved.map((t) => (
                <li key={t.id}>
                  <button data-testid={`template-option-${t.name}`} onClick={() => choose(t)} className={`w-full text-left px-3 py-2.5 transition-colors ${sel?.id === t.id ? "bg-slate-900 text-white" : "hover:bg-slate-50"}`}>
                    <div className="text-sm font-semibold font-mono truncate">{t.name}</div>
                    <div className={`text-[10px] uppercase tracking-wider ${sel?.id === t.id ? "text-slate-300" : "text-slate-400"}`}>{t.category} · {t.language}</div>
                  </button>
                </li>
              ))}
            </ul>
            {sel ? (
              <div className="space-y-3">
                {params.map((p, i) => (
                  <Input key={i} data-testid={`template-param-${i + 1}`} placeholder={`Variabile {{${i + 1}}}`} value={p}
                    onChange={(e) => setParams(params.map((x, j) => (j === i ? e.target.value : x)))} />
                ))}
                <div className="chat-wallpaper rounded-lg p-4">
                  <div data-testid="template-preview" className="bg-[#DCFCE7] border border-[#86EFAC] text-[#064E3B] rounded-xl rounded-tr-sm px-3.5 py-2 text-sm ml-auto max-w-[90%] whitespace-pre-wrap">{render(sel.body, params)}</div>
                </div>
                <Button data-testid="template-send-button" disabled={busy || params.some((p) => !p.trim())} onClick={send} className="w-full bg-[#25D366] hover:bg-[#22C55E] text-white">
                  <Send size={15} className="mr-1.5" />{busy ? "Invio…" : "Invia modello"}
                </Button>
              </div>
            ) : (
              <div className="grid place-items-center text-sm text-slate-400 border border-dashed border-slate-200 rounded-lg">Seleziona un modello</div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
